import { useMemo } from "react";
import { Download, X, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePWA } from "@/hooks/usePWA";
import { cn } from "@/lib/utils";

interface VersionUpdateBannerProps {
  serverVersion: string;
  localVersion: string;
  onDismiss: () => void;
  className?: string;
}

export function VersionUpdateBanner({
  serverVersion,
  localVersion,
  onDismiss,
  className,
}: VersionUpdateBannerProps) {
  const { forceUpdate, isUpdating } = usePWA();

  // Format versions for display
  const formattedVersions = useMemo(() => {
    const formatDate = (isoString: string) => {
      try {
        const date = new Date(isoString);
        return date.toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return isoString;
      }
    };

    return {
      local: formatDate(localVersion),
      server: formatDate(serverVersion),
    };
  }, [localVersion, serverVersion]);

  // Don't show banner while updating - it will reload anyway
  if (isUpdating) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg animate-in slide-in-from-top duration-300",
        className
      )}
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-8 w-8 rounded-full bg-white/20">
              <Download className="h-4 w-4" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4">
              <span className="font-medium text-sm sm:text-base">
                Nova versão disponível!
              </span>
              <span className="text-xs sm:text-sm text-white/80">
                Sua versão: {formattedVersions.local} → Nova: {formattedVersions.server}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={forceUpdate}
              disabled={isUpdating}
              className="bg-white text-orange-600 hover:bg-white/90 font-medium"
            >
              <RefreshCw className={cn("h-4 w-4 mr-1.5", isUpdating && "animate-spin")} />
              {isUpdating ? "Atualizando..." : "Atualizar Agora"}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onDismiss}
              className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/20"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
